import React, { useEffect, useState } from "react";
import {
  Box, Chip, CircularProgress, InputAdornment, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EventIcon from "@mui/icons-material/Event";
import FilterListIcon from "@mui/icons-material/FilterList";
import { VendorAssignment, vendorGetAssignments } from "../vendorApi";

const T = {
  navy: "#0f172a", gold: "#d4a373", white: "#fff",
  bg: "#f1f5f9", slate: "#64748b", slateLt: "#94a3b8", line: "#e2e8f0",
  greenTx: "#166534", greenBg: "#dcfce7",
  blueTx: "#1e40af", blueBg: "#dbeafe",
  amberTx: "#92400e", amberBg: "#fef3c7",
};

const inputSx = {
  "& .MuiOutlinedInput-root": { fontSize: 13, borderRadius: "10px", bgcolor: "#f8fafc", "& fieldset": { borderColor: T.line }, "&.Mui-focused fieldset": { borderColor: T.gold, borderWidth: 1.5 } },
};

const PRICING_LABELS: Record<string, string> = { per_event: "Per Event", per_hour: "Per Hour", per_unit: "Per Unit", per_plate: "Per Plate" };

const formatCurrency = (v: number) =>
  `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const VendorAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<VendorAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      vendorGetAssignments({ filter, search: search || undefined })
        .then(setAssignments)
        .catch(() => setAssignments([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [filter, search]);

  const totalValue = assignments.reduce((sum, a) => sum + a.final_price, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: T.navy, letterSpacing: "-0.02em" }}>
          Assignments
        </Typography>
        <Typography sx={{ fontSize: 14, color: T.slate, mt: 0.3 }}>
          View your assigned items and event details
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slate }}>Showing</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: T.navy, mt: 0.5 }}>{assignments.length}</Typography>
          <Typography sx={{ fontSize: 11, color: T.slateLt }}>assignments</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slate }}>Total Value</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: T.navy, mt: 0.5 }}>{formatCurrency(totalValue)}</Typography>
          <Typography sx={{ fontSize: 11, color: T.slateLt }}>combined total</Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", border: `1px solid ${T.line}`, display: { xs: "none", md: "block" } }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.slate }}>Unique Events</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: T.navy, mt: 0.5 }}>
            {new Set(assignments.map(a => a.booking_id)).size}
          </Typography>
          <Typography sx={{ fontSize: 11, color: T.slateLt }}>events assigned</Typography>
        </Paper>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <TextField size="small" placeholder="Search events or items..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, maxWidth: 360, flex: 1 }} />
        <TextField size="small" select value={filter} onChange={e => setFilter(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 14, color: T.slateLt }} /></InputAdornment> }}
          sx={{ ...inputSx, minWidth: 150 }}>
          <MenuItem value="upcoming">Upcoming</MenuItem>
          <MenuItem value="past">Past</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
      </Box>

      {/* Assignments Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress size={28} sx={{ color: T.gold }} />
        </Box>
      ) : assignments.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", borderRadius: "14px", border: `1px solid ${T.line}` }}>
          <AssignmentOutlinedIcon sx={{ fontSize: 48, color: T.slateLt, opacity: 0.4, mb: 1.5 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.navy, mb: 0.5 }}>No assignments found</Typography>
          <Typography sx={{ fontSize: 13, color: T.slateLt }}>
            {filter === "upcoming" ? "You have no upcoming assignments." : "No assignments match your search."}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {["Event", "Date", "Item", "Qty", "Unit Price", "Total", "Notes"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em", py: 1.5, borderBottom: `1px solid ${T.line}` }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map(a => (
                  <TableRow key={a.id} sx={{ "&:hover": { bgcolor: "rgba(212,163,115,0.04)" } }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: T.blueBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <EventIcon sx={{ fontSize: 14, color: T.blueTx }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{a.event_name}</Typography>
                          {a.booking_ref && <Typography sx={{ fontSize: 10, color: T.slateLt }}>{a.booking_ref}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: T.navy, py: 1.5 }}>
                      {a.event_date ? new Date(a.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy }}>{a.item_name}</Typography>
                      {a.service_name && <Typography sx={{ fontSize: 9, color: T.slateLt }}>{a.service_name}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600, color: T.navy, py: 1.5 }}>{a.quantity || "-"}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: T.slate, py: 1.5 }}>
                      {formatCurrency(a.rate)}
                      <Chip label={PRICING_LABELS[a.pricing_type] || a.pricing_type} size="small" sx={{ height: 14, fontSize: 7, ml: 0.5, bgcolor: `rgba(212,163,115,0.1)`, color: T.gold }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700, color: T.navy, py: 1.5 }}>{formatCurrency(a.final_price)}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: T.slate, py: 1.5, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.notes || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default VendorAssignments;
